import React, { useEffect, useState, Suspense, lazy } from 'react';
import { LanguageProvider } from './contexts/LanguageContext';
import { Navbar } from './components/Navbar';
import { Hero } from './components/Hero';
import { Mission, MissionPanels } from './components/Mission';
import { ConsularBanner } from './components/ConsularBanner';
import { LandingPage } from './components/LandingPage';
import { FoundingFundersPopup } from './components/FoundingFundersPopup';
import { GoogleAnalytics } from './components/GoogleAnalytics';
import { useLanguage } from './contexts/LanguageContext';
// Lazy-loaded below-the-fold sections
const CommunityStrength = lazy(() => import('./components/CommunityStrength').then(m => ({
  default: m.CommunityStrength
})));
const Leadership = lazy(() => import('./components/Leadership').then(m => ({
  default: m.Leadership
})));
const AdvisoryCouncil = lazy(() => import('./components/AdvisoryCouncil').then(m => ({
  default: m.AdvisoryCouncil
})));
const MakeDifferenceCTA = lazy(() => import('./components/MakeDifferenceCTA').then(m => ({
  default: m.MakeDifferenceCTA
})));
const NewsBlog = lazy(() => import('./components/NewsBlog').then(m => ({
  default: m.NewsBlog
})));
const MediaScroller = lazy(() => import('./components/MediaScroller').then(m => ({
  default: m.MediaScroller
})));
const ConsularAffairs = lazy(() => import('./components/ConsularAffairs').then(m => ({
  default: m.ConsularAffairs
})));
const BusinessInvestments = lazy(() => import('./components/BusinessInvestments').then(m => ({
  default: m.BusinessInvestments
})));
const OurPeopleStories = lazy(() => import('./components/OurPeopleStories').then(m => ({
  default: m.OurPeopleStories
})));
const NonprofitDirectory = lazy(() => import('./components/NonprofitDirectory').then(m => ({
  default: m.NonprofitDirectory
})));
const ExploreHonduras = lazy(() => import('./components/ExploreHonduras').then(m => ({
  default: m.ExploreHonduras
})));
const HondurasQuiz = lazy(() => import('./components/HondurasQuiz').then(m => ({
  default: m.HondurasQuiz
})));
const FAQ = lazy(() => import('./components/FAQ').then(m => ({
  default: m.FAQ
})));
const Footer = lazy(() => import('./components/Footer').then(m => ({
  default: m.Footer
})));
const SocialMediaVoices = lazy(() => import('./components/SocialMediaVoices').then(m => ({
  default: m.SocialMediaVoices
})));
const ChatbaseWidget = lazy(() => import('./components/ChatbaseWidget').then(m => ({
  default: m.ChatbaseWidget
})));
// Lazy-loaded sub-pages
const AdminDashboard = lazy(() => import('./components/AdminDashboard').then(m => ({
  default: m.AdminDashboard
})));
const ConsularPage = lazy(() => import('./pages/ConsularPage').then(m => ({
  default: m.ConsularPage
})));
const BusinessMapPage = lazy(() => import('./pages/BusinessMapPage').then(m => ({
  default: m.BusinessMapPage
})));
const MemberProfilePage = lazy(() => import('./pages/MemberProfilePage').then(m => ({
  default: m.MemberProfilePage
})));
const NewsStoryPage = lazy(() => import('./pages/NewsStoryPage').then(m => ({
  default: m.NewsStoryPage
})));
const MembersDirectoryPage = lazy(() => import('./pages/MembersDirectoryPage').then(m => ({
  default: m.MembersDirectoryPage
})));
const SaveTheDatePage = lazy(() => import('./pages/SaveTheDatePage').then(m => ({
  default: m.SaveTheDatePage
})));
const NationalNetworkPage = lazy(() => import('./pages/NationalNetworkPage').then(m => ({
  default: m.NationalNetworkPage
})));
const EncuentroCatrachoMiamiPage = lazy(() => import('./pages/EncuentroCatrachoMiamiPage').then(m => ({
  default: m.EncuentroCatrachoMiamiPage
})));
const VoicesOfImpactNYPage = lazy(() => import('./pages/VoicesOfImpactNYPage').then(m => ({
  default: m.VoicesOfImpactNYPage
})));
const HonduranIndependenceDayPage = lazy(() => import('./pages/HonduranIndependenceDayPage').then(m => ({
  default: m.HonduranIndependenceDayPage
})));
// Lightweight loading placeholder — subtle pulse instead of spinner to reduce visual jank
function SectionLoader() {
  return <div className="w-full py-16 flex items-center justify-center" data-id="element-0" data-mp="c8rglwcgvfy5">
      <div className="flex items-center gap-2" data-id="element-1" data-mp="c6eo4p931n6u">
        <div className="w-2 h-2 rounded-full bg-haa-blue/30 animate-pulse" data-id="element-2" data-mp="19uyjn1t4h08e" />
        <div className="w-2 h-2 rounded-full bg-haa-blue/20 animate-pulse" style={{
        animationDelay: '150ms'
      }} data-id="element-3" data-mp="14x6jjkibq7t4" />
        <div className="w-2 h-2 rounded-full bg-haa-blue/10 animate-pulse" style={{
        animationDelay: '300ms'
      }} data-id="element-4" data-mp="zzwtq3r0vu4r" />
      </div>
    </div>;
}
function AppContent({
  showLanding,
  setShowLanding
}: {
  showLanding: boolean;
  setShowLanding: (v: boolean) => void;
}) {
  const {
    setLanguage
  } = useLanguage();
  const [pendingScroll, setPendingScroll] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    const hash = window.location.hash;
    return isSectionAnchor(hash) ? resolveAnchor(hash.slice(1)) : null;
  });
  const handleSelectLanguage = (lang: 'EN' | 'ES') => {
    setLanguage(lang);
    setShowLanding(false);
  };
  const handleConsularAccess = (lang: 'EN' | 'ES') => {
    setLanguage(lang);
    setShowLanding(false);
    setPendingScroll('consular-affairs');
  };
  // React to in-page hash changes (e.g. opening a /#leadership link while already on the site)
  useEffect(() => {
    const handler = () => {
      const hash = window.location.hash;
      if (isSectionAnchor(hash)) {
        setShowLanding(false);
        setPendingScroll(resolveAnchor(hash.slice(1)));
      }
    };
    window.addEventListener('hashchange', handler);
    return () => window.removeEventListener('hashchange', handler);
  }, [setShowLanding]);
  useEffect(() => {
    if (!showLanding && pendingScroll) {
      // Lazy-loaded sections may not be mounted yet — retry briefly until the element exists.
      const start = Date.now();
      let cancelled = false;
      const tryScroll = () => {
        if (cancelled) return;
        const el = document.getElementById(pendingScroll);
        if (el) {
          el.scrollIntoView({
            behavior: 'smooth'
          });
          setPendingScroll(null);
          return;
        }
        if (Date.now() - start < 4000) {
          setTimeout(tryScroll, 150);
        } else {
          setPendingScroll(null);
        }
      };
      const timer = setTimeout(tryScroll, 100);
      return () => {
        cancelled = true;
        clearTimeout(timer);
      };
    }
  }, [showLanding, pendingScroll]);
  if (showLanding) {
    return <LandingPage onSelectLanguage={handleSelectLanguage} onConsularAccess={handleConsularAccess} data-id="element-5" data-mp="33g4m9bgiracz" />;
  }
  return <div className="font-nunito w-full min-h-screen overflow-x-hidden" data-id="element-6" data-mp="2yfqfrxukg8h2">
      <Navbar data-id="element-7" data-mp="3i4xzso2dw2xd" />
      <FoundingFundersPopup data-id="element-8" data-mp="3i4egb2x356h8" />
      <main data-id="element-9" data-mp="3n2pprcjgxeyx">
        <Hero data-id="element-10" data-mp="3n2g39yxmtpxz" />
        <ConsularBanner data-id="element-11" data-mp="3n2zjnb7mw69w" />
        <Mission data-id="element-12" data-mp="3n27mhvye20hi" />

        {/* Each section loads independently for faster perceived performance */}
        <Suspense fallback={<SectionLoader data-id="element-14" data-mp="387jjcoggne52" />} data-id="element-13" data-mp="384xb0mlqwuls">
          <OurPeopleStories data-id="element-15" data-mp="3d49ztcw6gbr0" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-17" data-mp="3d6mosa0crmdk" />} data-id="element-16" data-mp="3d2ywtfak380y">
          <MakeDifferenceCTA data-id="element-18" data-mp="2zdewsh8d0ovc" />
        </Suspense>
        <div className="max-w-5xl mx-auto px-4" data-id="element-19" data-mp="2zbke79kk98uf">
          <div className="h-px bg-gradient-to-r from-transparent via-haa-blue/20 to-transparent" data-id="element-20" data-mp="343lmkkelyff6" />
        </div>
        <Suspense fallback={<SectionLoader data-id="element-22" data-mp="2pg0lsx57gsaf" />} data-id="element-21" data-mp="2pfh8xuuewu39">
          <NewsBlog data-id="element-23" data-mp="2pgsbjrc4fvss" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-25" data-mp="2uf3owc1mkofr" />} data-id="element-24" data-mp="2uditylbef3hd">
          <MediaScroller data-id="element-26" data-mp="2uebs4xwakknw" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-28" data-mp="2ge10vtr2l427" />} data-id="element-27" data-mp="2gej7nkbcz0od">
          <CommunityStrength data-id="element-29" data-mp="2gesw33gr8zry" />
        </Suspense>

        <Suspense fallback={<SectionLoader data-id="element-31" data-mp="2kg4eop17okqn" />} data-id="element-30" data-mp="2kfmkdxy40gld">
          <MissionPanels data-id="element-32" data-mp="2kfcv6ckw2dgq" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-34" data-mp="30vnb80g7vcbv" />} data-id="element-33" data-mp="30v3tokuhapyd">
          <Leadership data-id="element-35" data-mp="30uvf8oipjcl2" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-37" data-mp="2vvr966r2xuyy" />} data-id="element-36" data-mp="2vwanldhiu188">
          <AdvisoryCouncil data-id="element-38" data-mp="2vxlsjgmpdf0x" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-40" data-mp="2qwob569iop0h" />} data-id="element-39" data-mp="2qx7hrqtpvqz7">
          <MakeDifferenceCTA data-id="element-41" data-mp="2qvweds46ol8m" />
        </Suspense>

        <Suspense fallback={<SectionLoader data-id="element-43" data-mp="2lymjvy1xtjdx" />} data-id="element-42" data-mp="2ly4o12j6t76j">
          <BusinessInvestments data-id="element-44" data-mp="2lxbfpx4ky9ma" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-46" data-mp="2hx1boq0clta2" />} data-id="element-45" data-mp="2hwj4wzg27wnw">
          <ConsularAffairs data-id="element-47" data-mp="2hxb0wbdkjwep" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-49" data-mp="2d59fnzyfavcj" />} data-id="element-48" data-mp="2d5t1vsv3w5wd">
          <SocialMediaVoices data-id="element-50" data-mp="2d514cchy7y3g" />
        </Suspense>

        <Suspense fallback={<SectionLoader data-id="element-52" data-mp="2866nhg3jkjlz" />} data-id="element-51" data-mp="286pvo53e3tmt">
          <NonprofitDirectory data-id="element-53" data-mp="287hqvet2rpck" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-55" data-mp="23852gpmk2a7v" />} data-id="element-54" data-mp="237n1xgsv19u5">
          <HondurasQuiz data-id="element-56" data-mp="238xzuzzmjndc" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-58" data-mp="1y91yl8ibaa1h" />} data-id="element-57" data-mp="1y8iqejigr00n">
          <ExploreHonduras data-id="element-59" data-mp="1y78pzno51fqu" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-61" data-mp="1u6f1as2vfwww" />} data-id="element-60" data-mp="1u6x6ie7ihlgy">
          <FAQ data-id="element-62" data-mp="1u889wcx1or7j" />
        </Suspense>
      </main>
      <Suspense fallback={null} data-id="element-63" data-mp="1u9b2qqdxh3q3">
        <Footer data-id="element-64" data-mp="9po0cvmlerzm" />
      </Suspense>
      <Suspense fallback={null} data-id="element-65" data-mp="9oczp4og20cq">
        <ChatbaseWidget data-id="element-66" data-mp="dqho9p01c6si" />
      </Suspense>
    </div>;
}
// Short, shareable aliases that resolve to the actual section IDs in the DOM.
// Example: /#council scrolls to the <section id="advisory-council">.
const SECTION_ALIASES: Record<string, string> = {
  council: 'advisory-council',
  members: 'our-stories',
  consular: 'consular-affairs',
  business: 'business-investments',
  nonprofit: 'nonprofit-directory',
  explore: 'explore-honduras',
  trivia: 'honduras-trivia',
  socials: 'social-voices'
};
// Section anchors that, when present in the URL hash, should bypass the landing page
// and scroll directly to that section on the main site. Both canonical IDs and the
// friendly aliases above are accepted.
const SECTION_ANCHORS = new Set(['home', 'mission', 'community', 'leadership', 'advisory-council', 'our-stories', 'news', 'social-voices', 'explore-honduras', 'honduras-trivia', 'nonprofit-directory', 'business-investments', 'consular-affairs', 'faq', 'contact', ...Object.keys(SECTION_ALIASES)]);
function resolveAnchor(id: string): string {
  return SECTION_ALIASES[id] ?? id;
}
function isSectionAnchor(hash: string): boolean {
  if (!hash || !hash.startsWith('#')) return false;
  if (hash.startsWith('#/')) return false;
  const id = hash.slice(1);
  return SECTION_ANCHORS.has(id);
}
export function App() {
  const initialHash = typeof window !== 'undefined' ? window.location.hash : '';
  const [showLanding, setShowLanding] = useState(!isSectionAnchor(initialHash));
  const [showAdmin, setShowAdmin] = useState(() => window.location.hash === '#/admin');
  const [showConsular, setShowConsular] = useState(() => window.location.hash === '#/consular');
  const [showBusinessMap, setShowBusinessMap] = useState(() => window.location.hash.startsWith('#/business-map'));
  const [businessMapLang, setBusinessMapLang] = useState<'EN' | 'ES'>(() => {
    const hash = window.location.hash;
    if (!hash.startsWith('#/business-map')) return 'EN';
    const match = hash.match(/[?&]lang=(EN|ES)/);
    return match ? match[1] as 'EN' | 'ES' : 'EN';
  });
  const [showMembers, setShowMembers] = useState(() => window.location.hash === '#/members');
  const [showSaveTheDate, setShowSaveTheDate] = useState(() => window.location.hash === '#/save-the-date');
  const [showVoicesNY, setShowVoicesNY] = useState(() => window.location.hash === '#/voices-of-impact-ny');
  const [showEncuentroMiami, setShowEncuentroMiami] = useState(() => window.location.hash === '#/encuentro-catracho-miami');
  const [showIndependenceDay, setShowIndependenceDay] = useState(() => window.location.hash === '#/independence-day');
  const [showNationalNetwork, setShowNationalNetwork] = useState(() => window.location.hash === '#/national-network');
  const [showMember, setShowMember] = useState(() => window.location.hash.startsWith('#/member/'));
  const [memberSlug, setMemberSlug] = useState(() => {
    const hash = window.location.hash;
    return hash.startsWith('#/member/') ? hash.replace('#/member/', '') : '';
  });
  const [showNews, setShowNews] = useState(() => window.location.hash.startsWith('#/news/'));
  const [newsPostId, setNewsPostId] = useState(() => {
    const hash = window.location.hash;
    if (!hash.startsWith('#/news/')) return '';
    const raw = hash.replace('#/news/', '');
    return raw.split('?')[0];
  });
  const [newsLang, setNewsLang] = useState<'EN' | 'ES'>(() => {
    const hash = window.location.hash;
    if (!hash.startsWith('#/news/')) return 'EN';
    const match = hash.match(/[?&]lang=(EN|ES)/);
    return match ? match[1] as 'EN' | 'ES' : 'EN';
  });
  useEffect(() => {
    const handleHash = () => {
      const hash = window.location.hash;
      setShowAdmin(hash === '#/admin');
      setShowConsular(hash === '#/consular');
      const isBusinessMap = hash.startsWith('#/business-map');
      setShowBusinessMap(isBusinessMap);
      if (isBusinessMap) {
        const bmLangMatch = hash.match(/[?&]lang=(EN|ES)/);
        setBusinessMapLang(bmLangMatch ? bmLangMatch[1] as 'EN' | 'ES' : 'EN');
      }
      setShowMembers(hash === '#/members');
      setShowSaveTheDate(hash === '#/save-the-date');
      setShowVoicesNY(hash === '#/voices-of-impact-ny');
      setShowEncuentroMiami(hash === '#/encuentro-catracho-miami');
      setShowIndependenceDay(hash === '#/independence-day');
      setShowNationalNetwork(hash === '#/national-network');
      const isMember = hash.startsWith('#/member/');
      setShowMember(isMember);
      if (isMember) {
        setMemberSlug(hash.replace('#/member/', ''));
      }
      const isNews = hash.startsWith('#/news/');
      setShowNews(isNews);
      if (isNews) {
        const raw = hash.replace('#/news/', '');
        setNewsPostId(raw.split('?')[0]);
        const langMatch = raw.match(/[?&]lang=(EN|ES)/);
        setNewsLang(langMatch ? langMatch[1] as 'EN' | 'ES' : 'EN');
      }
      // When navigating back to root from a sub-page, skip the landing page
      if (hash === '' || hash === '#') {
        setShowLanding(false);
      }
    };
    window.addEventListener('hashchange', handleHash);
    return () => window.removeEventListener('hashchange', handleHash);
  }, []);
  const handleReturnToLanding = () => {
    setShowLanding(true);
  };
  if (showAdmin) {
    return <Suspense fallback={<SectionLoader data-id="element-68" data-mp="10juvtwwvauvy" />} data-id="element-67" data-mp="10lfhex17f3ho">
        <AdminDashboard onBack={() => {
        window.location.hash = '';
        setShowAdmin(false);
      }} data-id="element-69" data-mp="15k8u4n8o2qgs" />
      </Suspense>;
  }
  if (showConsular) {
    return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-70" data-mp="1edlzionx3kjr">
        <Suspense fallback={<SectionLoader data-id="element-72" data-mp="1jcfc8evdr7iv" />} data-id="element-71" data-mp="1eewz0caby652">
          <ConsularPage data-id="element-73" data-mp="1jd88un0mkgna" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showBusinessMap) {
    return <LanguageProvider initialLanguage={businessMapLang} onLanguageSwitch={handleReturnToLanding} data-id="element-74" data-mp="1obrvyqqyfyh8">
        <Suspense fallback={<SectionLoader data-id="element-76" data-mp="1tg3whxf9iqwe" />} data-id="element-75" data-mp="1yd42y9g0xvo1">
          <BusinessMapPage data-id="element-77" data-mp="1tfub6n55x7x1" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showMembers) {
    return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-78" data-mp="1ohsl3i9bk8c2">
        <Suspense fallback={<SectionLoader data-id="element-80" data-mp="1kgh3o00lmtey" />} data-id="element-79" data-mp="1kfxj0bjkdqx8">
          <MembersDirectoryPage data-id="element-81" data-mp="1kh8nyeoqx4px" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showVoicesNY) {
    return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-82" data-mp="2h7iyfp34n674">
        <Suspense fallback={<SectionLoader data-id="element-84" data-mp="2c8oiqw0ghxr8" />} data-id="element-83" data-mp="2c75397mmjqqi">
          <VoicesOfImpactNYPage data-id="element-85" data-mp="27fn0ce11jg2w" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showEncuentroMiami) {
    return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-86" data-mp="23d8i9w574kco">
        <Suspense fallback={<SectionLoader data-id="element-88" data-mp="2zwpjhire89w3" />} data-id="element-87" data-mp="2zw66mgglobox">
          <EncuentroCatrachoMiamiPage data-id="element-89" data-mp="2zwzb1as36p3w" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showIndependenceDay) {
    return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-90" data-mp="2v3mqpcufx4gy">
        <Suspense fallback={<SectionLoader data-id="element-92" data-mp="ta0ec4jaxh9p" />} data-id="element-91" data-mp="t8fjedt2rwbb">
          <HonduranIndependenceDayPage data-id="element-93" data-mp="y6qt8ojde6tj" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showNationalNetwork) {
    return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-94" data-mp="jh2thrqu7g7x">
        <Suspense fallback={<SectionLoader data-id="element-96" data-mp="ogfb9y2upx3n" />} data-id="element-95" data-mp="ogxjlt2sg1rx">
          <NationalNetworkPage data-id="element-97" data-mp="ofmffs5fks0a" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showSaveTheDate) {
    return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-98" data-mp="ajil7yuk4jnt">
        <Suspense fallback={<SectionLoader data-id="element-100" data-mp="fhtyyknz3ebb" />} data-id="element-99" data-mp="fhaeaw6xubtl">
          <SaveTheDatePage data-id="element-101" data-mp="j9psn8e329h" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showMember) {
    return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-102" data-mp="5ht9si3fa3z7">
        <Suspense fallback={<SectionLoader data-id="element-104" data-mp="1uxiyer4rmegj" />} data-id="element-103" data-mp="5k629p6t3wqi">
          <MemberProfilePage slug={memberSlug} data-id="element-105" data-mp="1uyc9236uho3y" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showNews) {
    return <LanguageProvider initialLanguage={newsLang} data-id="element-106" data-mp="1zvkss87n607b">
        <Suspense fallback={<SectionLoader data-id="element-108" data-mp="3kfg5bn3pt35a" />} data-id="element-107" data-mp="3kdvby0t4zq90">
          <NewsStoryPage postId={newsPostId} data-id="element-109" data-mp="3keo9cb67h3eh" />
        </Suspense>
      </LanguageProvider>;
  }
  return <>
      <GoogleAnalytics data-id="element-110" data-mp="3fdh2qffsua9e" />
      <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-111" data-mp="3u4py756dupug">
        <AppContent showLanding={showLanding} setShowLanding={setShowLanding} data-id="element-112" data-mp="3u6aq0n1bbuom" />
      </LanguageProvider>
    </>;
}